import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Chip,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { WarningAmber, Close } from "@mui/icons-material";
import Shop from "../shop";
import { AppContext } from "../../context/AppContext";
import ImageGallery from "./ProductPage/ImageGallery";
import RelatedProduct from "./ProductPage/RelatedProduct";

export default function ProductDetail() {
  const { sectionSlug, categorySlug, productSlug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useContext(AppContext);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [galleryImgs, setGalleryImgs] = useState([]);

  const getDiscounted = (base, tipo, valor) => {
    if (!tipo || !valor) return Number(base);
    if (tipo === "descuento") return Number(base) * (1 - Number(valor) / 100);
    if (tipo === "precio_fijo") return Number(valor);
    return Number(base);
  };

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await fetch(`/productos/ver/${productSlug}/`);
        const data = await res.json();

        const base = {
          id: data.id,
          code: data.code,
          name: data.name,
          slug: data.slug,
          section: data.section,
          category: data.category,
          brand: data.brand,
          description: data.description || "",
          images: data.images || [],
          pvp1: Number(data.pvp1 ?? 0),
          precio_descuento: data.precio_descuento != null ? Number(data.precio_descuento) : null,
          iva_porcentaje: Number(data.iva_porcentaje ?? 0),
          iva_valor: Number(data.iva_valor ?? 0),
          iva_aplicado_porcentaje: Number(data.iva_aplicado_porcentaje ?? data.iva_porcentaje ?? 0),
          iva_aplicado_valor: Number(data.iva_aplicado_valor ?? data.iva_valor ?? 0),
          promo: { tipo: data.tipo_promocion || null, valor: data.valor_promocion ?? null },
          type: data.type,
          variant_slug: data.variant_slug || null,
          variants: (data.variants || []).map((v) => ({
            ...v,
            price: Number(v.price),
            iva_porcentaje: Number(v.iva_porcentaje ?? data.iva_porcentaje ?? 0),
            iva_valor: Number(v.iva_valor ?? data.iva_valor ?? 0),
            iva_aplicado_porcentaje: Number(v.iva_aplicado_porcentaje ?? v.iva_porcentaje ?? 0),
            iva_aplicado_valor: Number(v.iva_aplicado_valor ?? v.iva_valor ?? 0),
          })),
        };

        setProduct(base);

        if (base.variants.length && base.variant_slug) {
          const v = base.variants.find((x) => x.slug === base.variant_slug) || null;
          setSelectedVariant(v);
          setGalleryImgs(v?.image ? [v.image, ...base.images] : base.images);
        } else {
          setSelectedVariant(null);
          setGalleryImgs(base.images);
        }
      } finally {
        setLoading(false);
      }
    }
    window.scrollTo(0, 0);
    load();
  }, [productSlug]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );

  if (!product)
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography variant="h5" color="error">Producto no encontrado</Typography>
      </Box>
    );

  const hasVariants = product.variants.length > 0;

  const rawUnitPrice = selectedVariant
    ? Number(selectedVariant.price ?? 0)
    : Number(product.pvp1 ?? 0);

  const rawFinalPrice = selectedVariant
    ? (selectedVariant.precio_descuento != null
        ? Number(selectedVariant.precio_descuento)
        : getDiscounted(rawUnitPrice, product.promo.tipo, product.promo.valor))
    : (product.precio_descuento != null
        ? Number(product.precio_descuento)
        : getDiscounted(rawUnitPrice, product.promo.tipo, product.promo.valor));

  const ivaPct = Number(
    selectedVariant?.iva_aplicado_porcentaje ??
      selectedVariant?.iva_porcentaje ??
      product.iva_aplicado_porcentaje ??
      product.iva_porcentaje ??
      0
  );

  const ivaVal = Number(
    selectedVariant?.iva_aplicado_valor ??
      selectedVariant?.iva_valor ??
      product.iva_aplicado_valor ??
      product.iva_valor ??
      0
  );

  const displayOriginal = rawUnitPrice + ivaVal;
  const displayFinal = rawFinalPrice + ivaVal;

  const total = displayFinal * quantity;

  const adjustQty = (d) => setQuantity((q) => Math.max(1, q + d));

  const addToCartHandler = () => {
    if (hasVariants && !selectedVariant) return;

    const item = {
      id: hasVariants ? `${product.id}-${selectedVariant.id}` : product.id,
      name: product.name,
      code: product.code,
      section: product.section,
      category: product.category,
      brand: product.brand,
      images: product.images,
      price: rawFinalPrice,
      unitPrice: rawFinalPrice,
      iva_porcentaje: ivaPct,
      iva_valor: ivaVal,
      total_real: rawFinalPrice + ivaVal,
      quantity,
      variant: hasVariants ? { type: selectedVariant.variant_type, value: selectedVariant.value } : null,
      sku: hasVariants ? selectedVariant.sku : null,
      promo: product.promo,
    };

    addToCart(item, quantity);
    setIsCartOpen(true);
  };

  const selectVariant = (v) => {
    setSelectedVariant(v);
    setQuantity(1);
    setGalleryImgs(v?.image ? [v.image, ...product.images] : product.images);
    navigate(`/categorias/${sectionSlug}/${categorySlug}/${v.slug}`, { replace: true });
  };

  const promoChip = () => {
    if (product.promo.tipo === "descuento")
      return (
        <Chip
          label={`Descuento ${product.promo.valor}%`}
          sx={{ bgcolor: "#d32f2f", color: "#fff", fontWeight: 700, position: "absolute", top: 8, left: 8 }}
        />
      );
    if (product.promo.tipo === "precio_fijo")
      return (
        <Chip
          label={`-$${product.promo.valor}`}
          sx={{ bgcolor: "#ed6c02", color: "#fff", fontWeight: 700, position: "absolute", top: 8, left: 8 }}
        />
      );
    if (product.promo.tipo === "destacado")
      return (
        <Chip
          label="Destacado"
          sx={{ bgcolor: "#1976d2", color: "#fff", fontWeight: 700, position: "absolute", top: 8, left: 8 }}
        />
      );
    if (product.promo.tipo === "mas_vendido")
      return (
        <Chip
          label="Más vendido"
          sx={{ bgcolor: "#2e7d32", color: "#fff", fontWeight: 700, position: "absolute", top: 8, left: 8 }}
        />
      );
    return null;
  };

  return (
    <>
      <Box sx={{ p: 4, maxWidth: 1200, mx: "auto", bgcolor: "#fff" }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ position: "relative" }}>
              {promoChip()}
              <ImageGallery images={galleryImgs} productName={product.name} />
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Typography variant="h5" sx={{ color: "#333", mb: 1 }}>
              {product.name}
            </Typography>

            <Typography variant="caption" sx={{ color: "#666", display: "block", fontSize: "0.75rem", mb: 1 }}>
              REF: {product.code}
            </Typography>

            {selectedVariant?.sku && (
              <Typography variant="caption" sx={{ color: "#666", mb: 1, display: "block", fontSize: "0.75rem" }}>
                SKU: {selectedVariant.sku}
              </Typography>
            )}

            <Typography variant="body1" sx={{ color: "#666", mb: 1 }}>
              {product.brand}
            </Typography>

            <Typography variant="body2" sx={{ color: "#999", mb: 2 }}>
              Precio incluye IVA
            </Typography>

            {hasVariants && (
              <>
                <Typography variant="body2" sx={{ color: "#999", mb: 1 }}>
                  {product.variants[0]?.variant_type}
                </Typography>
                <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                  <Select
                    value={selectedVariant?.id || ""}
                    displayEmpty
                    onChange={(e) => {
                      const v = product.variants.find((x) => x.id === e.target.value);
                      selectVariant(v);
                    }}
                    renderValue={(sel) =>
                      !sel ? (
                        <Typography sx={{ color: "#999" }}>
                          Seleccionar {product.variants[0]?.variant_type}
                        </Typography>
                      ) : (
                        product.variants.find((v) => v.id === sel).value
                      )
                    }
                  >
                    <MenuItem value="" disabled>
                      Seleccionar {product.variants[0]?.variant_type}
                    </MenuItem>
                    {product.variants.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {(!hasVariants || selectedVariant) && (
              <>
                {rawUnitPrice > rawFinalPrice && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#9e9e9e",
                      textDecoration: "line-through",
                    }}
                  >
                    US$ {displayOriginal.toFixed(2)}
                  </Typography>
                )}

                <Typography variant="h4" sx={{ color: "#1565C0", fontWeight: 700, mb: 2 }}>
                  US$ {displayFinal.toFixed(2)}
                </Typography>
              </>
            )}

            <Typography variant="body2" sx={{ color: "#999", mb: 1 }}>
              Cantidad
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <IconButton onClick={() => adjustQty(-1)} sx={{ bgcolor: "#f0f0f0" }}>
                <RemoveIcon />
              </IconButton>
              <Typography variant="h6">{quantity}</Typography>
              <IconButton onClick={() => adjustQty(1)} sx={{ bgcolor: "#f0f0f0" }}>
                <AddIcon />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="body2" sx={{ color: "#999", mb: 1 }}>
              Precio Total
            </Typography>

            <Typography variant="h5" sx={{ color: "#1565C0", mb: 0.5 }}>
              US$ {total.toFixed(2)}
            </Typography>

            <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 2 }}>
              Precio incluye IVA
            </Typography>

            <Button
              variant="contained"
              sx={{
                bgcolor: "#007B83",
                color: "#fff",
                py: 1,
                mb: 2,
                width: { xs: "100%", md: "80%" },
              }}
              startIcon={<ShoppingCartIcon />}
              disabled={hasVariants && !selectedVariant}
              onClick={addToCartHandler}
            >
              Añadir al Carrito
            </Button>
          </Grid>
        </Grid>

        {showAlert && (
          <Box
            sx={{
              p: 2,
              mt: 6,
              mb: 3,
              border: "1px solid #ffb74d",
              backgroundColor: "#fff3e0",
              borderRadius: 2,
              display: "flex",
              alignItems: "flex-start",
              position: "relative",
            }}
          >
            <WarningAmber sx={{ color: "#ff9800", mr: 1, mt: "3px" }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: "bold", color: "#333" }}>
                El cliente es responsable por entender y averiguar sobre la descripción del producto.
              </Typography>
              <Typography variant="body2" sx={{ color: "#333" }}>
                No nos hacemos responsables si realizas una compra sin leer detalladamente la descripción.{" "}
                <Button
                  onClick={() => navigate("/terminos-condiciones")}
                  variant="text"
                  sx={{
                    color: "#1565C0",
                    textTransform: "none",
                    fontWeight: "bold",
                    p: 0,
                    minWidth: 0,
                  }}
                >
                  Ver más en TYC
                </Button>
              </Typography>
            </Box>
            <IconButton sx={{ position: "absolute", right: 8, top: 8 }} onClick={() => setShowAlert(false)}>
              <Close />
            </IconButton>
          </Box>
        )}

        <Box sx={{ maxWidth: "100%", mx: "auto", mt: 4 }}>
          <Typography variant="h6" sx={{ color: "#333", mb: 2 }}>
            Descripción
          </Typography>
          <Typography variant="body1" sx={{ color: "#555" }}>
            {String(product.description || "")
              .split("\n")
              .map((l, i) => (
                <span key={i}>
                  {l}
                  <br />
                </span>
              ))}
          </Typography>
        </Box>
      </Box>

      <RelatedProduct productId={product.id} />
      <Shop isOpen={isCartOpen} toggleDrawer={() => setIsCartOpen(!isCartOpen)} />
    </>
  );
}
